import * as THREE from 'three'
import { mergeGeoms } from './creatures'

// 3D meshes for every map object (scenery props, solid obstacles, destructible loot) — replaces the old
// billboarded prop sprites with chunky low-poly primitives so the whole environment reads as 3D. Each distinct
// sprite name gets its own instanced batch (one draw call), built once; geometries sit with their base on the
// ground (y=0) and a height of ~1 unit so the sim's per-prop `scale` maps straight to world height.

const _m = new THREE.Matrix4()
const _q = new THREE.Quaternion()
const _yAxis = new THREE.Vector3(0, 1, 0)
const _pos = new THREE.Vector3()
const _scl = new THREE.Vector3()
const _col = new THREE.Color()

interface PropDef {
  build: () => THREE.BufferGeometry
  color?: number // single-colour props; multi-part builders bake per-vertex colours instead
  flat?: boolean
  emissive?: number
  smooth?: boolean
}

// a low-poly rounded rock (flattened icosahedron), base on the ground
function rock(scaleY = 0.8): THREE.BufferGeometry {
  const g = new THREE.IcosahedronGeometry(0.5, 0)
  g.scale(0.95, scaleY, 0.95)
  g.translate(0, 0.42 * scaleY + 0.04, 0)
  return g
}
function box(w: number, h: number, d: number): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, h, d)
  g.translate(0, h / 2, 0)
  return g
}
function cyl(rt: number, rb: number, h: number): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(rt, rb, h, 12)
  g.translate(0, h / 2, 0)
  return g
}
function sphere(sx: number, sy: number): THREE.BufferGeometry {
  const g = new THREE.SphereGeometry(0.5, 14, 11)
  g.scale(sx, sy, sx)
  g.translate(0, 0.5 * sy, 0)
  return g
}
function octa(): THREE.BufferGeometry {
  const g = new THREE.OctahedronGeometry(0.6, 0)
  g.scale(0.7, 1.15, 0.7)
  g.translate(0, 0.7, 0)
  return g
}
// ---- multi-part landmarks (baked vertex colours; PropBatch switches to vertexColors when a colour attr is present) ----
function microwaveGeo(): THREE.BufferGeometry {
  const body = new THREE.BoxGeometry(0.95, 0.62, 0.66); body.translate(0, 0.43, 0)
  const win = new THREE.BoxGeometry(0.5, 0.42, 0.04); win.translate(-0.12, 0.43, 0.34) // dark front window
  const panel = new THREE.BoxGeometry(0.18, 0.5, 0.04); panel.translate(0.34, 0.43, 0.34) // control panel
  const footL = new THREE.BoxGeometry(0.1, 0.14, 0.1); footL.translate(-0.38, 0.07, 0.24)
  const footR = new THREE.BoxGeometry(0.1, 0.14, 0.1); footR.translate(0.38, 0.07, 0.24)
  return mergeGeoms([
    { geo: body, color: 0xb9bec8 }, { geo: win, color: 0x18324a }, { geo: panel, color: 0x39d6ff },
    { geo: footL, color: 0x40454e }, { geo: footR, color: 0x40454e },
  ])
}
function moaiGeo(): THREE.BufferGeometry {
  const base = new THREE.BoxGeometry(0.5, 0.4, 0.42); base.translate(0, 0.2, 0)
  const head = new THREE.BoxGeometry(0.56, 0.66, 0.5); head.scale(1, 1, 1); head.translate(0, 0.74, 0.02)
  const browL = new THREE.BoxGeometry(0.16, 0.1, 0.06); browL.translate(-0.13, 0.86, 0.26)
  const browR = new THREE.BoxGeometry(0.16, 0.1, 0.06); browR.translate(0.13, 0.86, 0.26)
  const nose = new THREE.BoxGeometry(0.12, 0.26, 0.1); nose.translate(0, 0.66, 0.27)
  return mergeGeoms([
    { geo: base, color: 0x8b8f96 }, { geo: head, color: 0x9aa0a8 },
    { geo: browL, color: 0x6d7178 }, { geo: browR, color: 0x6d7178 }, { geo: nose, color: 0x7e838b },
  ])
}
// a pedestal + a glowing orb on top; orb colour set per shrine type
function shrineGeo(orbColor: number): THREE.BufferGeometry {
  const base = new THREE.CylinderGeometry(0.34, 0.42, 0.34, 12); base.translate(0, 0.17, 0)
  const stem = new THREE.CylinderGeometry(0.12, 0.16, 0.42, 10); stem.translate(0, 0.5, 0)
  const orb = new THREE.SphereGeometry(0.26, 14, 12); orb.translate(0, 0.86, 0)
  return mergeGeoms([{ geo: base, color: 0x9aa0aa }, { geo: stem, color: 0x7d828c }, { geo: orb, color: orbColor }])
}
function portalGeo(): THREE.BufferGeometry {
  const ring = new THREE.TorusGeometry(0.42, 0.12, 10, 20); ring.rotateX(Math.PI / 2); ring.translate(0, 0.7, 0)
  const base = new THREE.CylinderGeometry(0.3, 0.4, 0.2, 12); base.translate(0, 0.1, 0)
  return mergeGeoms([{ geo: base, color: 0x4a3a6a }, { geo: ring, color: 0xb98cff }])
}

// Per sprite-name look. Heights baked to ~1 unit so the sim `scale` reads as world height.
const PROP_CONFIG: Record<string, PropDef> = {
  'prop:moonrock': { build: () => rock(0.78), color: 0x9aa0aa, flat: true },
  'prop:rock': { build: () => rock(0.8), color: 0x888d96, flat: true },
  'prop:redrock': { build: () => rock(0.86), color: 0xb0673e, flat: true },
  'prop:debris': { build: () => rock(0.6), color: 0x6f7480, flat: true },
  'prop:crater': { build: () => cyl(0.62, 0.7, 0.16), color: 0x55585f, flat: true },
  'prop:cactus': { build: () => cyl(0.18, 0.22, 1.0), color: 0x4f9e54, smooth: true },
  'prop:antenna': { build: () => cyl(0.04, 0.07, 1.0), color: 0xb4bac4, flat: true },
  'prop:satellite': { build: () => box(0.5, 0.5, 0.5), color: 0xb8bdc8, flat: true },
  'prop:flag': { build: () => cyl(0.04, 0.05, 1.0), color: 0xc6ccd6, flat: true },
  'prop:grave': { build: () => box(0.62, 0.92, 0.26), color: 0x949aa3, flat: true },
  'prop:deadtree': { build: () => cyl(0.05, 0.2, 1.0), color: 0x6b4a2f, flat: true },
  'prop:pillar': { build: () => cyl(0.26, 0.32, 1.0), color: 0xa8aeb8, flat: true },
  'prop:crystal': { build: () => octa(), color: 0x9b6cff, emissive: 0x5b2fb0, smooth: true },
  'prop:crate': { build: () => box(0.7, 0.7, 0.7), color: 0xb07a3f, flat: true },
  'break:crate': { build: () => box(0.7, 0.7, 0.7), color: 0xb07a3f, flat: true },
  'break:pod': { build: () => sphere(0.78, 0.92), color: 0x6fae5a, smooth: true },
  'break:barrel': { build: () => cyl(0.34, 0.34, 0.9), color: 0xc05038, flat: true },
  // landmarks / interactables (multi-part, baked vertex colours)
  'prop:microwave': { build: microwaveGeo, flat: true },
  'prop:moai': { build: moaiGeo, flat: true },
  'prop:portal': { build: portalGeo, smooth: true, emissive: 0x6a3acc },
  'prop:shrine': { build: () => shrineGeo(0x9fd0ff), flat: true },
  'prop:shrine_magnet': { build: () => shrineGeo(0xff5a5a), flat: true },
  'prop:shrine_gold': { build: () => shrineGeo(0xffd23d), flat: true },
  'prop:shrine_heal': { build: () => shrineGeo(0x5be08a), flat: true },
  'prop:shrine_treasure': { build: () => shrineGeo(0xffe27a), flat: true },
  'prop:shrine_battle': { build: () => shrineGeo(0xff8a3d), flat: true },
  'prop:shrine_curse': { build: () => shrineGeo(0xb46cff), flat: true },
}

/** One instanced batch of a single prop type. begin() → add(...) per visible instance → end(). */
export class PropBatch {
  readonly mesh: THREE.InstancedMesh
  private count = 0
  private readonly cap: number

  constructor(def: PropDef, capacity: number) {
    this.cap = capacity
    const geo = def.build()
    if (def.flat) geo.computeVertexNormals() // keep faceted; (icosa/box already faceted)
    const hasVColor = !!geo.getAttribute('color') // multi-part landmarks bake their own colours
    const mat = new THREE.MeshStandardMaterial({
      color: hasVColor ? 0xffffff : (def.color ?? 0x9aa0aa),
      vertexColors: hasVColor,
      roughness: 0.92,
      metalness: 0.0,
      flatShading: !!def.flat,
    })
    if (def.emissive !== undefined) { mat.emissive = new THREE.Color(def.emissive); mat.emissiveIntensity = 0.5 }
    this.mesh = new THREE.InstancedMesh(geo, mat, capacity)
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
    this.mesh.castShadow = true
    this.mesh.receiveShadow = true
    this.mesh.frustumCulled = false
    this.mesh.count = 0
    this.mesh.setColorAt(0, _col.setHex(0xffffff))
  }

  begin(): void { this.count = 0 }

  /** (x,y,z) feet position; `height` world units; `rotY` yaw; `bright` (1 normal, >1 flash) via instanceColor. */
  add(x: number, y: number, z: number, height: number, rotY: number, bright: number): void {
    if (this.count >= this.cap) return
    const i = this.count++
    _pos.set(x, y, z)
    _q.setFromAxisAngle(_yAxis, rotY)
    _scl.set(height, height, height)
    _m.compose(_pos, _q, _scl)
    this.mesh.setMatrixAt(i, _m)
    this.mesh.setColorAt(i, _col.setRGB(bright, bright, bright))
  }

  end(): void {
    this.mesh.count = this.count
    this.mesh.instanceMatrix.needsUpdate = true
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true
  }
}

/** Build a batch per known prop type. Returns the map + a fallback key for unknown sprites. */
export function buildPropBatches(capacityEach = 256): { batches: Record<string, PropBatch>; has: (sprite: string) => boolean } {
  const batches: Record<string, PropBatch> = {}
  for (const key of Object.keys(PROP_CONFIG)) batches[key] = new PropBatch(PROP_CONFIG[key], capacityEach)
  return { batches, has: (s: string) => s in batches }
}
